import { useEffect, useRef, useState, type ReactNode } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
  }
}

const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
export const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? '';

/** The script once per page: every widget waits for the same load. */
let loading: Promise<void> | undefined;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => {
      // A script that failed does not load again: gone, so that the next widget tries anew
      // instead of waiting for an answer that never comes (review of SP-D20).
      script.remove();
      loading = undefined;
      reject(new Error('turnstile'));
    });
    script.src = SCRIPT;
    script.async = true;
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * Cloudflare Turnstile (privacy-friendly, no cookies); shown only when the API asks for it. The
 * `action` names what the token confirms – the API refuses a token of another action (SP-D20).
 * When Cloudflare's script does not load, `fallback` takes the place of an empty box.
 */
export function Turnstile({
  onToken,
  language,
  action,
  fallback = null,
}: {
  onToken: (token: string) => void;
  language: string;
  action: 'login' | 'quick_check';
  fallback?: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let id: string | undefined;
    let cancelled = false;
    void loadScript()
      .then(() => {
        if (cancelled || !box.current || !window.turnstile) return;
        id = window.turnstile.render(box.current, {
          sitekey: TURNSTILE_SITE_KEY,
          language,
          action,
          callback: onToken,
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (id) window.turnstile?.remove(id);
    };
  }, [onToken, language, action]);
  return failed ? fallback : <div ref={box} />;
}
